import { useState, useEffect, useCallback } from 'react';
import {
  SpaceBetween,
  Button,
  Input,
  Container,
  Header,
  Box,
  FormField,
  Select,
  Textarea,
} from '@cloudscape-design/components';

interface HolidayDatesEditorProps {
  values: Record<string, unknown>;
  onChange: (values: Record<string, unknown>) => void;
}

interface HolidayEntry {
  date: string;
  description: string;
}

const DATE_PATTERN = /^\d{2}\/\d{2}\/\d{4}$/;
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Determines if a key looks like a date entry in the holiday payload.
 * EA Portal stores holidays as "MM/DD/YYYY": "Description" or "YYYY-MM-DD": "Description".
 * Excludes known non-date keys.
 */
const NON_DATE_KEYS = new Set(['timeZone', 'timezone', 'message', 'action', 'startDate', 'endDate', '_parentPath', '_businessUnit']);

function isDateKey(key: string): boolean {
  if (NON_DATE_KEYS.has(key)) return false;
  // Temporary new holiday placeholders (must check before generic underscore filter)
  if (key.startsWith('__new_holiday_')) return true;
  if (key.startsWith('_')) return false;
  // Match MM/DD/YYYY or YYYY-MM-DD patterns
  return DATE_PATTERN.test(key) || ISO_DATE_PATTERN.test(key);
}

/**
 * Holiday Dates Editor — renders holiday date entries as an editable table.
 * 
 * The EA Portal stores holidays as key-value pairs in the attributes:
 *   "01/01/2026": "New Years (Thurs)"
 *   "12/25/2025": "Christmas Day"
 *   "timeZone": "Pacific/Auckland"
 * 
 * This component renders those as a table with Date | Description columns
 * and provides Add/Remove capability.
 *
 * Also renders timeZone, message, and action fields if present.
 */
export function HolidayDatesEditor({ values, onChange }: HolidayDatesEditorProps) {
  const [timezone, setTimezone] = useState((values.timeZone as string) ?? (values.timezone as string) ?? '');
  const [message, setMessage] = useState((values.message as string) ?? '');
  const [action, setAction] = useState((values.action as string) ?? '');
  const [holidays, setHolidays] = useState<HolidayEntry[]>([]);
  const [initialized, setInitialized] = useState(false);

  // Initialize holidays from payload on first load
  useEffect(() => {
    if (initialized) return;
    const entries: HolidayEntry[] = [];
    for (const [key, val] of Object.entries(values)) {
      if (isDateKey(key) && typeof val === 'string') {
        const date = key.startsWith('__new_holiday_') ? '' : key;
        entries.push({ date, description: val });
      }
    }
    entries.sort((a, b) => {
      if (!a.date && b.date) return 1;
      if (a.date && !b.date) return -1;
      return a.date.localeCompare(b.date);
    });
    setHolidays(entries);
    setTimezone((values.timeZone as string) ?? (values.timezone as string) ?? '');
    setMessage((values.message as string) ?? '');
    setAction((values.action as string) ?? '');
    setInitialized(true);
  }, [values, initialized]);

  // Push changes to parent whenever local state changes
  const syncToParent = useCallback((newHolidays: HolidayEntry[], newTimezone?: string, newMessage?: string, newAction?: string) => {
    const payload: Record<string, unknown> = {};
    payload.timeZone = newTimezone ?? timezone;
    const msg = newMessage ?? message;
    if (msg) payload.message = msg;
    const act = newAction ?? action;
    if (act) payload.action = act;
    // Holiday date entries
    let emptyIndex = 0;
    for (const entry of newHolidays) {
      if (entry.date && entry.date.trim()) {
        payload[entry.date] = entry.description;
      } else {
        payload[`__new_holiday_${emptyIndex++}`] = entry.description;
      }
    }
    if (values._parentPath) {
      payload._parentPath = values._parentPath;
    }
    onChange(payload);
  }, [onChange, timezone, message, action, values._parentPath]);

  const addHoliday = () => {
    const newHolidays = [...holidays, { date: '', description: '' }];
    setHolidays(newHolidays);
    syncToParent(newHolidays);
  };

  const removeHoliday = (index: number) => {
    const newHolidays = holidays.filter((_, i) => i !== index);
    setHolidays(newHolidays);
    syncToParent(newHolidays);
  };

  const updateHoliday = (index: number, field: 'date' | 'description', value: string) => {
    const newHolidays = [...holidays];
    newHolidays[index] = { ...newHolidays[index], [field]: value };
    setHolidays(newHolidays);
    syncToParent(newHolidays);
  };

  const getDateError = (date: string): string | undefined => {
    if (!date) return undefined;
    if (!DATE_PATTERN.test(date) && !ISO_DATE_PATTERN.test(date)) {
      return 'Use MM/DD/YYYY or YYYY-MM-DD format';
    }
    return undefined;
  };

  return (
    <SpaceBetween size="l">
      {/* Timezone */}
      <Container header={<Header variant="h3">Holiday Schedule Settings</Header>}>
        <SpaceBetween size="m">
          <FormField label="Timezone">
            <Input
              value={timezone}
              onChange={({ detail }) => { setTimezone(detail.value); syncToParent(holidays, detail.value); }}
              placeholder="e.g. Pacific/Auckland"
            />
          </FormField>

          <FormField label="Closure Message" description="Message played to callers during a holiday closure">
            <Textarea
              value={message}
              onChange={({ detail }) => { setMessage(detail.value); syncToParent(holidays, undefined, detail.value); }}
              placeholder="Optional closure message"
            />
          </FormField>

          <FormField label="Action" description="Action taken when holiday is active">
            <Select
              selectedOption={action ? { label: action.charAt(0).toUpperCase() + action.slice(1), value: action } : null}
              onChange={({ detail }) => { const val = detail.selectedOption?.value ?? ''; setAction(val); syncToParent(holidays, undefined, undefined, val); }}
              options={[
                { label: 'Resume', value: 'resume' },
                { label: 'Disconnect', value: 'disconnect' },
                { label: 'Transfer', value: 'transfer' },
              ]}
              placeholder="Select..."
            />
          </FormField>
        </SpaceBetween>
      </Container>

      {/* Holiday Dates Table */}
      <Container
        header={
          <Header
            variant="h3"
            actions={<Button onClick={addHoliday} iconName="add-plus">Add Holiday</Button>}
            counter={`(${holidays.length})`}
          >
            Holiday Dates
          </Header>
        }
      >
        {holidays.length === 0 ? (
          <Box textAlign="center" color="text-body-secondary" padding="l">
            No holiday dates configured. Click "Add Holiday" to add one.
          </Box>
        ) : (
          <SpaceBetween size="s">
            {/* Header row */}
            <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr 60px', gap: '8px', alignItems: 'center', padding: '0 0 4px 0', borderBottom: '1px solid #E2E8F0' }}>
              <Box variant="awsui-key-label">Date</Box>
              <Box variant="awsui-key-label">Description</Box>
              <Box variant="awsui-key-label">Remove</Box>
            </div>

            {/* Date rows */}
            {holidays.map((entry, index) => {
              const dateError = getDateError(entry.date);
              return (
                <div key={index} style={{ display: 'grid', gridTemplateColumns: '180px 1fr 60px', gap: '8px', alignItems: 'start' }}>
                  <FormField errorText={dateError}>
                    <Input
                      value={entry.date}
                      onChange={({ detail }) => updateHoliday(index, 'date', detail.value)}
                      placeholder="MM/DD/YYYY"
                    />
                  </FormField>
                  <Input
                    value={entry.description}
                    onChange={({ detail }) => updateHoliday(index, 'description', detail.value)}
                    placeholder="e.g. Christmas Day"
                  />
                  <Button
                    variant="icon"
                    iconName="remove"
                    onClick={() => removeHoliday(index)}
                    ariaLabel={`Remove ${entry.description || 'holiday'}`}
                  />
                </div>
              );
            })}
          </SpaceBetween>
        )}
      </Container>
    </SpaceBetween>
  );
}
